import { realizedRevenue, type OutcomeAmountStatus, type RevenueRow } from "./revenue";
import { DAY_MS } from "./window";

/**
 * Revenue attribution for a campaign wave (Agent Brains section 5.11, produces
 * line: "Campaign waves and their measured ROI.")
 *
 * NOT A TRUE ROI. Nothing in this schema records what a wave cost: no ad
 * spend, no per-send cost ledger row, no gateway-metered compose cost tagged
 * to a campaign (the T3 literal path this build uses for the send itself has
 * none; see `campaignSweep.ts`). A ratio needs two sides, and this file has
 * only the revenue side. Calling that number `roi` would misname a partial
 * measurement as the whole thing the charter's word implies, which is exactly
 * the kind of arithmetic CLAUDE.md section 4 reserves for deterministic code,
 * never a comforting label. So every field here says `attributedRevenueCents`,
 * never `roi`, and this doc comment says so again for whoever reaches for the
 * field expecting a ratio.
 *
 * NOT EXCLUSIVE ACROSS CAMPAIGNS. Two different waves can both claim credit
 * for the same sale if their attribution windows overlap on the same thread:
 * this is last-touch-per-campaign, not a single shared attribution model that
 * arbitrates between competing waves. A shared, mutually exclusive model would
 * need to know about every OTHER campaign's sends when scoring one, which is
 * real complexity this build does not add. Logged in FINDINGS.md TA-183.
 *
 * TRANSMITTED SENDS ONLY (G37, plan 2 D10; CLAUDE.md section 13 F-06: "`sent`
 * (gates passed) and `transmitted` (left the building) are distinct fields,
 * never conflated"). A `campaign_sends` row is a send the gates passed; a
 * wave that never left the building cannot have earned a sale. Revenue is
 * attributed over transmitted sends only, and the untransmitted ones are
 * counted beside the figure (`untransmitted`), never folded into it. Nothing
 * transmits a wave today, so every wave reads `transmitted: 0`.
 *
 * Pure function, no IO, the same split `buildOutcomeDigest` (outcomeDigest.ts)
 * already establishes: this takes pre-fetched rows and does the arithmetic in
 * deterministic code. The DB-querying half lives in
 * `packages/db/src/campaignRoi.ts`.
 */

/**
 * How long after a wave send a sale on the same thread still counts as this
 * wave's revenue.
 *
 * Thirty days is a common last-touch marketing-attribution standard (it is
 * roughly the shortest window most attribution literature treats as
 * defensible for a considered purchase, which jewelry almost always is), not
 * a number measured against this shop's own sales cycle, because no wave has
 * shipped yet to measure one against. AWAITING RATIFICATION, the same status
 * `REVIEW_ASK_DELAY_MS` and `FOLLOWUP_SILENCE_MS` carry: an implementer's
 * conservative placeholder, not a number Mike or Kabir has seen.
 */
export const CAMPAIGN_ATTRIBUTION_WINDOW_MS = 30 * DAY_MS;

export interface CampaignSendRow {
  readonly contactId: string;
  readonly threadId: string;
  readonly sentAt: Date;
  /** Whether this send left the building (F-06). A gate-passed send that was not transmitted earns no revenue. */
  readonly transmitted: boolean;
}

export interface CampaignOutcomeRow {
  readonly outcomeId: string;
  readonly threadId: string;
  readonly label: string;
  /** Null is a real, common state (a non-monetary outcome, or a sale whose amount
   * the owner did not know). Never counted as zero revenue. */
  readonly amountCents: number | null;
  /** `stated` | `unknown` on a sold or deposit row (migration 0060), null on a label that carries no amount. */
  readonly amountStatus: OutcomeAmountStatus | null;
  readonly recordedAt: Date;
  /** Ghost auto-labels, and any outcome, can reverse (B6). A reversed row
   * never counts toward revenue: money that un-happened is not revenue. */
  readonly reversedAt: Date | null;
}

export interface CampaignAttributionInput {
  readonly campaignId: string;
  readonly sends: readonly CampaignSendRow[];
  readonly outcomes: readonly CampaignOutcomeRow[];
  readonly windowMs?: number;
}

export interface CampaignAttribution {
  readonly campaignId: string;
  readonly windowMs: number;
  readonly sendsCount: number;
  /** Sends that left the building (F-06): the only ones revenue is attributed over. */
  readonly transmitted: number;
  /** Sends the gates passed that never left the building: counted beside the figure, never attributed. */
  readonly untransmitted: number;
  /** How many threads' amounts contributed to the sum (one per thread per send, by the one revenue rule). Reported
   * alongside the sum so a caller can tell "zero sales" apart from "zero because everything qualifying had an
   * unknown amount", the same honesty `buildOutcomeDigest` gives its own overflow count. */
  readonly attributedOutcomeCount: number;
  /** Realized revenue: `saleRevenueCents + depositRevenueCents`. Never a deposit AND the sale that followed it. */
  readonly attributedRevenueCents: number;
  /** Sales counted once, at the sold amount. */
  readonly saleRevenueCents: number;
  /** Deposits on threads with no standing sale in the window, labelled apart from sales. */
  readonly depositRevenueCents: number;
  /** Threads whose sale or deposit amount is not known: counted apart, never as zero. */
  readonly attributedAmountUnknown: number;
  /** Deposits not added on top because the same thread has a standing sale in the window. */
  readonly depositsAbsorbed: number;
  /** Standing rows beyond the one counted per thread and label, so the reading is never silent. */
  readonly extraStandingRows: number;
}

/**
 * For each TRANSMITTED send (G37, F-06; an untransmitted one is only counted), the unreversed outcomes on the SAME thread that landed at or after the send and within `windowMs`
 * of it (both edges in) are folded through the one revenue rule, `realizedRevenue` (revenue.ts, B2 note 3): a sale
 * counts once at its own amount, a deposit followed by a sale is not added on top, a deposit alone is deposit
 * revenue, an unknown amount is counted apart and never as zero. The window and the per-send claim are unchanged
 * (two sends on one thread each claim it, last-touch-per-send); only the per-thread amount rule changed from
 * "add every amount". Measures revenue attributable to the wave, not "did the wave close exactly one sale."
 */
export function attributeCampaignRevenue(input: CampaignAttributionInput): CampaignAttribution {
  const windowMs = input.windowMs ?? CAMPAIGN_ATTRIBUTION_WINDOW_MS;

  // core-duplication/campaign-roi-nested-loop: index the outcomes by thread once instead of scanning every
  // outcome for every send. Reversed rows are dropped at index time; the fold drops them again if one slips in.
  const byThread = new Map<string, CampaignOutcomeRow[]>();
  for (const o of input.outcomes) {
    if (o.reversedAt !== null) continue;
    let rows = byThread.get(o.threadId);
    if (rows === undefined) {
      rows = [];
      byThread.set(o.threadId, rows);
    }
    rows.push(o);
  }

  let saleRevenueCents = 0;
  let depositRevenueCents = 0;
  let attributedOutcomeCount = 0;
  let attributedAmountUnknown = 0;
  let depositsAbsorbed = 0;
  let extraStandingRows = 0;

  let transmitted = 0;
  for (const s of input.sends) {
    // G37 (F-06): a send that never left the building earns nothing.
    if (!s.transmitted) continue;
    transmitted += 1;
    const rows = byThread.get(s.threadId);
    if (rows === undefined) continue;
    const sentMs = s.sentAt.getTime();
    const windowEnd = sentMs + windowMs;
    const inWindow: RevenueRow[] = [];
    for (const o of rows) {
      const at = o.recordedAt.getTime();
      if (at < sentMs) continue;
      if (at > windowEnd) continue;
      inWindow.push({
        outcomeId: o.outcomeId,
        threadId: o.threadId,
        label: o.label,
        amountCents: o.amountCents,
        amountStatus: o.amountStatus,
        recordedAt: o.recordedAt,
        reversedAt: o.reversedAt,
      });
    }
    if (inWindow.length === 0) continue;
    const r = realizedRevenue(inWindow);
    saleRevenueCents += r.sale.cents;
    depositRevenueCents += r.deposit.cents;
    attributedOutcomeCount += r.sale.count + r.deposit.count;
    attributedAmountUnknown += r.sale.amountUnknown + r.deposit.amountUnknown;
    depositsAbsorbed += r.depositsAbsorbed;
    extraStandingRows += r.extraRows;
  }

  return {
    campaignId: input.campaignId,
    windowMs,
    sendsCount: input.sends.length,
    transmitted,
    untransmitted: input.sends.length - transmitted,
    attributedOutcomeCount,
    attributedRevenueCents: saleRevenueCents + depositRevenueCents,
    saleRevenueCents,
    depositRevenueCents,
    attributedAmountUnknown,
    depositsAbsorbed,
    extraStandingRows,
  };
}
